// src/components/admin/DateMigrationTool.jsx
import React, { useState } from 'react';
import { AlertCircle, CheckCircle, RefreshCw, ArrowLeft } from 'lucide-react';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';

const DateMigrationTool = ({ onBack }) => {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [completed, setCompleted] = useState(false);

  // Convert MM-DD-YY to MM-DD-YYYY
  const convertDate = (oldDate) => {
    try {
      if (!oldDate || typeof oldDate !== 'string') {
        return { success: false, error: 'Invalid input' };
      }

      const parts = oldDate.split('-');
      if (parts.length !== 3) {
        return { success: false, error: 'Invalid format' };
      }

      const month = parts[0];
      const day = parts[1];
      let year = parts[2];

      // If already 4-digit year, return as-is
      if (year.length === 4) {
        return { success: true, oldDate, newDate: oldDate, alreadyCorrect: true };
      }

      // Convert 2-digit year to 4-digit
      // Assume 00-49 = 2000-2049, 50-99 = 1950-1999
      if (year.length === 2) {
        const yearNum = parseInt(year);
        year = yearNum < 50 ? `20${year}` : `19${year}`;
      } else {
        return { success: false, error: 'Invalid year format' };
      }

      const newDate = `${month}-${day}-${year}`;
      
      // Validate the date
      const testDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      if (isNaN(testDate.getTime())) {
        return { success: false, error: 'Invalid date' };
      }

      return { success: true, oldDate, newDate, alreadyCorrect: false };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const scanSchedules = async () => {
    setLoading(true);
    try {
      const schedulesRef = collection(db, 'schedules');
      const snapshot = await getDocs(schedulesRef);
      
      const results = [];
      snapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        if (data.date) {
          const conversion = convertDate(data.date);
          results.push({
            id: docSnap.id,
            staffName: data.staffName || 'Unknown',
            location: data.location || 'N/A',
            programType: data.programType || 'N/A',
            date: data.date,
            conversion
          });
        }
      });

      const successful = results.filter(r => r.conversion.success && !r.conversion.alreadyCorrect).length;
      const alreadyCorrect = results.filter(r => r.conversion.success && r.conversion.alreadyCorrect).length;
      const failed = results.filter(r => !r.conversion.success).length;

      setPreview({ results, successful, alreadyCorrect, failed, total: results.length });
    } catch (error) {
      console.error('Scan error:', error);
      alert('Failed to scan schedules: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const runMigration = async () => {
    if (!window.confirm(`
⚠️ WARNING: This will update ${preview.successful} schedule dates in your database.

This action cannot be easily undone. Are you sure you want to continue?

Click OK to proceed, or Cancel to abort.
    `)) {
      return;
    }

    setLoading(true);
    let successCount = 0;
    let errorCount = 0;
    const errors = [];

    try {
      const needsUpdate = preview.results.filter(r => r.conversion.success && !r.conversion.alreadyCorrect);

      for (const schedule of needsUpdate) {
        try {
          const scheduleRef = doc(db, 'schedules', schedule.id);
          await updateDoc(scheduleRef, {
            date: schedule.conversion.newDate,
            migratedAt: new Date().toISOString()
          });
          successCount++;
          console.log(`✅ Updated ${schedule.staffName}: ${schedule.conversion.oldDate} → ${schedule.conversion.newDate}`);
        } catch (error) {
          errorCount++;
          errors.push({ id: schedule.id, error: error.message });
          console.error(`❌ Failed to update ${schedule.id}:`, error);
        }
      }

      setCompleted(true);
      alert(`
✅ Migration Complete!

✅ Successfully updated: ${successCount}
❌ Failed: ${errorCount}
${errorCount > 0 ? '\n\nCheck console for error details.' : ''}
      `);

      // Refresh preview
      await scanSchedules();

    } catch (error) {
      console.error('Migration error:', error);
      alert('Migration failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '1rem', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <button
          onClick={onBack}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1.5rem',
            backgroundColor: '#6b7280',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '0.875rem',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          <ArrowLeft size={16} />
          Back to Settings
        </button>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <RefreshCw size={32} style={{ color: '#3b82f6' }} />
            Schedule Date Format Migration
          </h1>
          <p style={{ color: '#6b7280' }}>
            Convert schedule dates from <code style={{ backgroundColor: '#f3f4f6', padding: '0.125rem 0.375rem', borderRadius: '4px' }}>MM-DD-YY</code> to <code style={{ backgroundColor: '#f3f4f6', padding: '0.125rem 0.375rem', borderRadius: '4px' }}>MM-DD-YYYY</code>
          </p>
        </div>

        <div style={{ backgroundColor: '#fef3c7', padding: '1rem', borderRadius: '8px', marginBottom: '2rem', border: '1px solid #fbbf24' }}>
          <div style={{ display: 'flex', alignItems: 'start', gap: '0.5rem' }}>
            <AlertCircle size={20} style={{ color: '#d97706', flexShrink: 0, marginTop: '2px' }} />
            <div style={{ fontSize: '0.875rem', color: '#92400e' }}>
              <strong>Important:</strong> This tool will scan all schedules in your database and convert 2-digit years to 4-digit years.
              Years 00-49 become 2000-2049, years 50-99 become 1950-1999.
              <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem' }}>
                <li>Always backup your database before running migrations</li>
                <li>This will update dates in the "schedules" collection</li>
                <li>Example: "01-02-26" will become "01-02-2026"</li>
              </ul>
            </div>
          </div>
        </div>

        {!preview && (
          <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', textAlign: 'center' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>
              Step 1: Scan Schedules
            </h3>
            <p style={{ color: '#6b7280', marginBottom: '2rem' }}>
              Click the button below to scan your schedules collection and preview what will be changed.
            </p>
            <button
              onClick={scanSchedules}
              disabled={loading}
              style={{
                padding: '1rem 2rem',
                backgroundColor: loading ? '#9ca3af' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? '🔍 Scanning...' : '🔍 Scan Schedules'}
            </button>
          </div>
        )}

        {preview && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
              <div style={{ padding: '1.5rem', backgroundColor: '#f3f4f6', borderRadius: '8px' }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1f2937' }}>
                  {preview.total}
                </div>
                <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>Total Schedules</div>
              </div>
              
              <div style={{ padding: '1.5rem', backgroundColor: '#dcfce7', borderRadius: '8px' }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#16a34a' }}>
                  {preview.successful}
                </div>
                <div style={{ color: '#15803d', fontSize: '0.875rem' }}>Need Update</div>
              </div>
              
              <div style={{ padding: '1.5rem', backgroundColor: '#dbeafe', borderRadius: '8px' }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3b82f6' }}>
                  {preview.alreadyCorrect}
                </div>
                <div style={{ color: '#1e40af', fontSize: '0.875rem' }}>Already Correct</div>
              </div>
              
              <div style={{ padding: '1.5rem', backgroundColor: '#fee2e2', borderRadius: '8px' }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#dc2626' }}>
                  {preview.failed}
                </div>
                <div style={{ color: '#991b1b', fontSize: '0.875rem' }}>Failed/Invalid</div>
              </div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>
                📋 Preview of Changes
              </h3>
              <div style={{ maxHeight: '400px', overflowY: 'auto', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ backgroundColor: '#f9fafb', position: 'sticky', top: 0 }}>
                    <tr>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontSize: '0.875rem' }}>Status</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontSize: '0.875rem' }}>Staff</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontSize: '0.875rem' }}>Program</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontSize: '0.875rem' }}>Current Date</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontSize: '0.875rem' }}>New Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.results.map((result, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '0.75rem' }}>
                          {result.conversion.success ? (
                            result.conversion.alreadyCorrect ? (
                              <CheckCircle size={16} style={{ color: '#3b82f6' }} />
                            ) : (
                              <CheckCircle size={16} style={{ color: '#16a34a' }} />
                            )
                          ) : (
                            <AlertCircle size={16} style={{ color: '#dc2626' }} />
                          )}
                        </td>
                        <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>
                          {result.staffName}
                        </td>
                        <td style={{ padding: '0.75rem', fontSize: '0.75rem', color: '#6b7280' }}>
                          {result.programType}
                        </td>
                        <td style={{ padding: '0.75rem', fontSize: '0.875rem', fontFamily: 'monospace' }}>
                          {result.date}
                        </td>
                        <td style={{ padding: '0.75rem', fontSize: '0.875rem', fontFamily: 'monospace', fontWeight: '500', color: result.conversion.success ? (result.conversion.alreadyCorrect ? '#3b82f6' : '#16a34a') : '#dc2626' }}>
                          {result.conversion.success ? 
                            (result.conversion.alreadyCorrect ? '✓ OK' : result.conversion.newDate) : 
                            result.conversion.error
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {preview.successful > 0 && !completed && (
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                <button
                  onClick={() => {
                    setPreview(null);
                    setCompleted(false);
                  }}
                  style={{
                    padding: '1rem 2rem',
                    backgroundColor: '#6b7280',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={runMigration}
                  disabled={loading}
                  style={{
                    padding: '1rem 2rem',
                    backgroundColor: loading ? '#9ca3af' : '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: loading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {loading ? '⏳ Migrating...' : `⚡ Run Migration (${preview.successful} updates)`}
                </button>
              </div>
            )}

            {completed && (
              <div style={{ backgroundColor: '#dcfce7', padding: '1.5rem', borderRadius: '8px', border: '2px solid #16a34a', textAlign: 'center' }}>
                <CheckCircle size={48} style={{ color: '#16a34a', margin: '0 auto 1rem auto' }} />
                <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#15803d', marginBottom: '0.5rem' }}>
                  Migration Complete!
                </h3>
                <p style={{ color: '#15803d', margin: 0 }}>
                  Your schedule dates have been successfully updated. You can now use the updated StaffSchedule component.
                </p>
                <button
                  onClick={onBack}
                  style={{
                    marginTop: '1rem',
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#16a34a',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Return to Settings
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DateMigrationTool;